import React from 'react';
import {
  Badge,
  CalendarIcon,
  Gutter,
  Layout,
  LayoutItem,
  ProfileStatusBadge,
} from '@axiom/ui';
import type { Candidate } from '@axiom/validation';
import { CandidatesConst, PositionsConst } from '@axiom/const';

import { DateUtil } from '../../utils/date-util';
import { NumberUtil } from '../../utils/number-util';
import type { UserBenchFilterType } from '../../api/user-talent-api';
import {
  UserBenchTalentSortKeys,
  UserBenchTalentTypes,
} from '../../api/user-talent-api';

// Named export to match filename
export const BenchViewGridCellsUtil = {};

type CellRenderProps = {
  data: Candidate;
};

export const lastNameCell = ({ data }: CellRenderProps) => {
  return data.calculatedDisplayName;
};

export const flaggedTalentCellRender = ({ data }: CellRenderProps) => {
  return data.isFlagged ? (
    <Badge background="red" name="FLAGGED_BADGE">
      Flagged
    </Badge>
  ) : (
    '-'
  );
};

export const profileStatusCellRender = ({ data }: CellRenderProps) => {
  return data.profileStatus ? (
    <ProfileStatusBadge
      profileStatus={data.profileStatus}
      name="PROFILE_STATUS_BADGE"
    />
  ) : (
    '-'
  );
};

export const isPriorityTalentCellRender = ({ data }: CellRenderProps) =>
  data.isPriorityTalent ? (
    <Badge background="forest green" name="PRIORITY_BADGE">
      Priority
    </Badge>
  ) : (
    '-'
  );

type TalentOwnershipCellProps = {
  data: Candidate;
  filters: UserBenchFilterType;
  userId: string;
};

export const talentOwnershipCell = ({
  data,
  filters,
  userId,
}: TalentOwnershipCellProps) => {
  if (!filters.type) {
    return data.ownerUserId === userId
      ? UserBenchTalentTypes.owner
      : UserBenchTalentTypes.collaborator;
  }

  return UserBenchTalentTypes[filters.type];
};

type PrimaryPracticeAreaCellProps = {
  data: Candidate;
  practiceAreas: Record<string, { fullName?: string }>;
};

export const primaryPracticeAreaCell = ({
  data,
  practiceAreas,
}: PrimaryPracticeAreaCellProps) => {
  return (
    data.primaryPracticeArea &&
    practiceAreas[data.primaryPracticeArea]?.fullName
  );
};

export const weeklyAvailabilityCell = ({ data }: CellRenderProps) => {
  return NumberUtil.formatAsHours(data.weeklyAvailability);
};

export const totalEngagedHoursCell = ({ data }: CellRenderProps) => {
  const hours = data.calculatedEngagedAndReservedHours?.totalEngagedHours ?? 0;

  if (hours >= CandidatesConst.EngagedHoursWarningLimit) {
    return (
      <Badge
        name="ENGAGED_HOURS_WARNING_BADGE"
        background="red"
      >{`${hours} hrs`}</Badge>
    );
  }
  return `${hours} hrs`;
};

export const totalReservedHoursCell = ({ data }: CellRenderProps) => {
  const hours = data.calculatedEngagedAndReservedHours?.totalReservedHours ?? 0;

  if (hours >= CandidatesConst.ReservedHoursWarningLimit) {
    return (
      <Badge
        name="RESERVED_HOURS_WARNING_BADGE"
        background="red"
      >{`${hours} hrs`}</Badge>
    );
  }
  return `${hours} hrs`;
};

export const availabilityPreferencesUpdatedAtCell = ({
  data,
}: CellRenderProps) => {
  if (data.availabilityPreferencesUpdatedAt) {
    const duration = DateUtil.getDaysSinceNow(
      data.availabilityPreferencesUpdatedAt
    );
    return `${duration} ${duration === 1 ? 'day' : 'days'}`;
  }
  return null;
};

type AccountNameCellProps = {
  data: Candidate;
  filters: UserBenchFilterType;
};

export const alphabeticallySmallestEngagementAccountNameCell = ({
  data,
  filters,
}: AccountNameCellProps) => {
  return filters.sort ===
    UserBenchTalentSortKeys.alphabeticallySmallestEngagementAccountName
    ? data.alphabeticallySmallestEngagementAccountName
    : data.soonestEngagementAccountName;
};

export const soonestEngagementEndDateCell = ({ data }: CellRenderProps) => {
  const endDate = data.soonestEngagementEndDate;

  return (
    <Layout
      name="ENGAGEMENT_END_DATE_LAYOUT"
      position="middle"
      horizontalGutter="4px"
    >
      {data.soonestEngagementEndDateStatus ===
        PositionsConst.EndDateStatuses.Confirmed && (
        <Gutter top="8px">
          <span title={PositionsConst.EndDateStatuses.Confirmed}>
            <CalendarIcon withCheck />
          </span>
        </Gutter>
      )}
      <LayoutItem name="ENGAGEMENT_END_DATE_ITEM">
        {DateUtil.displayDate(endDate)}
      </LayoutItem>
    </Layout>
  );
};
